import * as React from 'react';
import type {
  ConfigEntityDetail,
  EntityDetail,
  DynamicEntityListTableColumn,
  ColumnRendererType,
} from '@web-config-app/core';
import { TextCell } from './text-cell';
import { ImageCell } from './image-cell';
import { IconCell } from './icon-cell';
import { StatusCell } from './status-cell';
import { RelativeTimeCell } from './relative-time-cell';
import { DateTimeCell } from './date-time-cell';
import { LinkedNameCell, SelectableNameCell } from './name-cell';
import { IdCell } from './id-cell';
import { RadioIndicatorCell } from './radio-indicator-cell';
import { CheckboxCell } from './checkbox-cell';
import { UrlCell } from './url-cell';

type EntityItem = ConfigEntityDetail | EntityDetail;

/**
 * Props passed to cell renderers by EntityListTable.
 *
 * This is a superset of all possible props - each renderer destructures
 * only the props it needs. Individual cell components define their own
 * specific prop types.
 */
export interface CellRendererProps {
  /** The entity data for this row */
  item: EntityItem;
  /** Resolved value from the column path (dynamic columns only) */
  value?: unknown;
  /** Column configuration (dynamic columns only) */
  col?: DynamicEntityListTableColumn;
  /** Link URL for linkable cells */
  linkTo?: string;
  /** Function to extract display name from entity */
  getItemName: (data: EntityItem) => string | undefined;
  /** Entity ID for display (id column) */
  itemId?: string;
  /** Selection change handler (selection columns) */
  onChange?: () => void;
  /** Whether selection is disabled (selection columns) */
  disabled?: boolean;
  /** Whether row is selected (checkbox column) */
  selected?: boolean;
}

export type ColumnRenderer = React.FC<CellRendererProps>;

export type RendererKey = ColumnRendererType;

export type SelectableColumnRendererKey = 'radioIndicator' | 'checkbox';

export type DefaultColumnRendererKey = Exclude<
  RendererKey,
  | 'image'
  | 'icon'
  | 'relativeTime'
  | 'dateTime'
  | 'text'
  | 'radioIndicator'
  | 'checkbox'
  | 'url'
>;

export const defaultColumnKeys: DefaultColumnRendererKey[] = [
  'name',
  'id',
  'date',
  'status',
];

export const defaultSelectableColumnKeys: Array<
  DefaultColumnRendererKey | SelectableColumnRendererKey
> = ['radioIndicator', ...defaultColumnKeys];

export const defaultMultiSelectableColumnKeys: Array<
  DefaultColumnRendererKey | SelectableColumnRendererKey
> = ['checkbox', ...defaultColumnKeys];

export const allDefaultColumnKeys = Array.from(
  new Set([
    ...defaultColumnKeys,
    ...defaultSelectableColumnKeys,
    ...defaultMultiSelectableColumnKeys,
  ]),
);

export const isDefaultColumnKey = (
  key: string,
): key is DefaultColumnRendererKey =>
  defaultColumnKeys.includes(key as DefaultColumnRendererKey);

export const isDefaultOrSelectableColumnKey = (
  key: string,
): key is DefaultColumnRendererKey | SelectableColumnRendererKey =>
  allDefaultColumnKeys.includes(key as DefaultColumnRendererKey);

/**
 * Shared renderers used by both linkable and selectable tables
 */
const sharedRenderers: Omit<Record<RendererKey, ColumnRenderer>, 'name'> = {
  text: ({ item, value, col, linkTo }) => (
    <TextCell
      item={item}
      value={value}
      linkTo={linkTo}
      col={
        col ?? {
          id: 'text',
          label: { text: '' },
          copyable: false,
          hideText: false,
        }
      }
    />
  ),
  image: ({ item, value, getItemName, linkTo }) => (
    <ImageCell
      item={item}
      value={value}
      getItemName={getItemName}
      linkTo={linkTo}
    />
  ),
  status: ({ item, linkTo }) => <StatusCell item={item} linkTo={linkTo} />,
  relativeTime: ({ item, linkTo }) => (
    <RelativeTimeCell item={item} linkTo={linkTo} />
  ),
  date: ({ item, linkTo }) => <RelativeTimeCell item={item} linkTo={linkTo} />,
  dateTime: ({ value, linkTo }) => (
    <DateTimeCell value={value} linkTo={linkTo} />
  ),
  id: ({ itemId, linkTo }) => <IdCell itemId={itemId} linkTo={linkTo} />,
  icon: ({ col, linkTo }) => <IconCell icon={col?.icon} linkTo={linkTo} />,
  radioIndicator: ({ item, onChange, disabled }) => (
    <RadioIndicatorCell item={item} onChange={onChange} disabled={disabled} />
  ),
  checkbox: ({ item, onChange, disabled, selected }) => (
    <CheckboxCell
      item={item}
      onChange={onChange}
      disabled={disabled}
      selected={selected}
    />
  ),
  url: ({ item, value }) => <UrlCell item={item} value={value} />,
};

/**
 * Registry for linkable tables - name column renders as a link
 */
export const linkableRendererRegistry: Record<RendererKey, ColumnRenderer> = {
  ...sharedRenderers,
  name: ({ item, linkTo, getItemName }) => (
    <LinkedNameCell item={item} linkTo={linkTo} getItemName={getItemName} />
  ),
};

/**
 * Registry for selectable tables - name column renders as a label for selection
 */
export const selectableRendererRegistry: Record<RendererKey, ColumnRenderer> = {
  ...sharedRenderers,
  name: ({ item, getItemName }) => (
    <SelectableNameCell item={item} getItemName={getItemName} />
  ),
};
